import 'swagger-ui-react/swagger-ui.css';

import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useEffect, useMemo, useRef, useState } from 'react';
import SwaggerUI from 'swagger-ui-react';

import { BookOpen, Copy, Download } from '@repo/media/icons';

import { Badge } from '@repo/components/badge';
import { Button } from '@repo/components/button';
import { ButtonLink } from '@repo/components/button-link';
import { Card } from '@repo/components/card';
import { LoadingIndicator } from '@repo/components/loading-indicator';
import { Select, SelectItem } from '@repo/components/select';
import { TextField } from '@repo/components/text-field';

import { addToast } from '@repo/features/toast';
import { minutesToMilliseconds } from '@repo/utils/time';

import { FeatureGuard } from '~/features/auth/feature-guard.tsx';
import { ContentHeader } from '~/features/content-header/content-header.tsx';
import { ALL_SERVICES_ID, docSpecUrl, gatewayBase, queryDocServices, tokenFingerprint } from '~/services/api-docs/api-docs';
import { queryOrganizationById } from '~/services/authorization/authorization.ts';
import { organization } from '~/services/authorization/types.ts';
import { getAccessToken } from '~/services/http';

export const Route = createFileRoute('/organization/$org/api-docs')({
  loader: async ({ context: { queryClient }, params: { org } }) => {
    return queryClient.ensureQueryData(queryOrganizationById(org));
  },
  component: RouteComponent,
});

type AuthMode = 'user' | 'service-principal';

// client_credentials authenticates the application itself, so it uses the resource's
// `.default` scope rather than the delegated scope the signed-in user holds.
const servicePrincipalScope = import.meta.env.VITE_API_SCOPE.replace(/\/[^/]+$/, '/.default');

// Hide Swagger UI's own Authorize button and lock icons; identity comes from the panel above.
function hideSwaggerAuthPlugin() {
  const Hidden = () => null;
  return {
    wrapComponents: {
      authorizeBtn: () => Hidden,
      AuthorizeOperationBtn: () => Hidden,
    },
  };
}

function RouteComponent() {
  const { org } = Route.useParams();
  const organizationData = useSuspenseQuery(queryOrganizationById(org)).data;

  return (
    <FeatureGuard features={organization('api-documentation:read')}>
      <ApiDocsPage organizationName={organizationData.name} tenantId={org} />
    </FeatureGuard>
  );
}

function ApiDocsPage({ organizationName, tenantId }: { organizationName: string; tenantId: string }) {
  const servicesQuery = useQuery(queryDocServices());
  const docServices = useMemo(() => servicesQuery.data ?? [], [servicesQuery.data]);
  const docOptions = useMemo(
    () => [{ id: ALL_SERVICES_ID, name: 'All services' }, ...docServices],
    [docServices],
  );
  const [selectedId, setSelectedId] = useState<string>(ALL_SERVICES_ID);

  // Identity for the document and for "Try it out": the signed-in user, or a service
  // principal whose token the developer obtains out of band and pastes in.
  const [authMode, setAuthMode] = useState<AuthMode>('user');
  const [clientId, setClientId] = useState('');
  const [bearerInput, setBearerInput] = useState('');
  const [servicePrincipalToken, setServicePrincipalToken] = useState<string | undefined>(undefined);

  const userTokenQuery = useQuery({
    queryKey: ['developer', 'api-access-token'],
    queryFn: getAccessToken,
    staleTime: minutesToMilliseconds(5),
  });

  const activeToken = authMode === 'service-principal' ? servicePrincipalToken : userTokenQuery.data;

  // A ref so the request interceptor always reads the latest token.
  const tokenRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    tokenRef.current = activeToken;
  }, [activeToken]);

  // The token request for the customer's own tenant. The secret is never typed here; the
  // developer fills it in where they run the request.
  const tokenEndpoint = `${import.meta.env.VITE_AZURE_AUTHORITY}/${tenantId}/oauth2/v2.0/token`;
  const servicePrincipalCurl = useMemo(() => (
    `curl -X POST '${tokenEndpoint}' \\
  -d 'grant_type=client_credentials' \\
  -d 'client_id=${clientId || '<your-application-client-id>'}' \\
  -d 'client_secret=<your-client-secret>' \\
  --data-urlencode 'scope=${servicePrincipalScope}'`
  ), [clientId, tokenEndpoint]);

  // Fetched here with the bearer attached: Swagger UI's own definition fetch would go
  // out anonymous and only ever get the public tier.
  const specQuery = useQuery({
    queryKey: ['developer', 'doc-spec', selectedId, authMode, activeToken ? tokenFingerprint(activeToken) : null],
    enabled: !(authMode === 'user' && userTokenQuery.isLoading),
    queryFn: async () => {
      const response = await fetch(docSpecUrl(selectedId), {
        headers: activeToken ? { Authorization: `Bearer ${activeToken}` } : {},
      });
      if (!response.ok) {
        throw new Error(`Failed to load the API document (${response.status}).`);
      }
      const spec = await response.json() as Record<string, unknown>;
      // Paths are gateway-relative; without an absolute server "Try it out" would hit the SPA origin.
      spec['servers'] = [{ url: gatewayBase }];
      return spec;
    },
  });

  const downloadSpec = () => {
    const data = specQuery.data;
    if (!data) {
      return;
    }
    const fileBase = selectedId === ALL_SERVICES_ID ? 'all' : selectedId;
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const href = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = href;
    link.download = `northern-lights-api-${fileBase}.json`;
    document.body.append(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(href);
  };

  const copyServicePrincipalCurl = () => {
    void globalThis.navigator.clipboard?.writeText(servicePrincipalCurl);
    addToast({ variant: 'positive', title: 'Copied', description: 'Token request copied to clipboard.' });
  };

  return (
    <div className="flex flex-col gap-6 pb-6">
      <div className="flex w-full flex-wrap items-start justify-between gap-4">
        <div className="flex flex-col gap-1">
          <ContentHeader>API documentation</ContentHeader>
          <p>
            The Northern Lights API reference for
            {' '}
            {organizationName}
            . Each document is filtered to what the active identity below may call.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <ButtonLink
            to="/organization/$org/guides/$guide"
            params={{ org: tenantId, guide: 'onboarding' }}
            variant="soft"
            color="primary"
            size="sm"
            icon={{ component: <BookOpen size={16} /> }}
          >
            Onboarding guide
          </ButtonLink>
          <ButtonLink
            to="/organization/$org/guides/$guide"
            params={{ org: tenantId, guide: 'integration' }}
            variant="soft"
            color="primary"
            size="sm"
            icon={{ component: <BookOpen size={16} /> }}
          >
            API integration guide
          </ButtonLink>
        </div>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <Select
          label="Service"
          className="min-w-72"
          items={docOptions}
          value={selectedId}
          onChange={(value) => { setSelectedId(value as string); }}
        >
          {option => <SelectItem id={option.id}>{option.name}</SelectItem>}
        </Select>
        <Button
          variant="soft"
          color="secondary"
          icon={{ component: <Download size={16} /> }}
          isDisabled={!specQuery.data}
          action={downloadSpec}
        >
          Download schema
        </Button>
        {servicesQuery.isError && (
          <Button variant="ghost" color="secondary" size="sm" action={() => { void servicesQuery.refetch(); }}>
            Retry loading services
          </Button>
        )}
      </div>

      <Card className="gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-medium text-secondary-800">Identity</p>
            <p className="text-sm text-secondary-500">Which identity the documents are filtered for and &ldquo;Try it out&rdquo; requests run as.</p>
          </div>
          <div className="flex items-center gap-2" role="group" aria-label="Identity">
            <Button
              size="md"
              variant={authMode === 'user' ? 'solid' : 'soft'}
              color={authMode === 'user' ? 'primary' : 'secondary'}
              action={() => { setAuthMode('user'); }}
            >
              Signed-in user
            </Button>
            <Button
              size="md"
              variant={authMode === 'service-principal' ? 'solid' : 'soft'}
              color={authMode === 'service-principal' ? 'primary' : 'secondary'}
              action={() => { setAuthMode('service-principal'); }}
            >
              Service account
            </Button>
          </div>
        </div>

        {authMode === 'user'
          ? (
              <div className="flex items-center gap-2 text-sm">
                {userTokenQuery.isSuccess
                  ? <Badge color="positive">Using your signed-in user token</Badge>
                  : userTokenQuery.isLoading
                    ? (
                        <span className="text-secondary-500 inline-flex items-center gap-2">
                          <LoadingIndicator size={14} className="text-primary-700" />
                          {' '}
                          Acquiring your token…
                        </span>
                      )
                    : <Badge color="negative">Could not acquire your token</Badge>}
              </div>
            )
          : (
              <div className="flex flex-col gap-5">
                <div className="flex flex-col gap-2">
                  <p className="text-sm font-medium text-secondary-700">1. Get a token for your service account</p>
                  <p className="text-sm text-secondary-500">
                    Run the request below from your own environment with the application&rsquo;s client id and secret, then paste the returned access token. The browser cannot request it for you.
                  </p>
                  <TextField
                    label="Application (client) id"
                    value={clientId}
                    onChange={setClientId}
                    placeholder="00000000-0000-0000-0000-000000000000"
                    autoComplete="off"
                  />
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs uppercase tracking-wider text-secondary-500">Token request</span>
                    <Button
                      size="sm"
                      variant="ghost"
                      color="secondary"
                      icon={{ component: <Copy size={14} /> }}
                      action={copyServicePrincipalCurl}
                    >
                      Copy
                    </Button>
                  </div>
                  <pre className="overflow-x-auto rounded-md border border-secondary-200 bg-secondary-50 p-3 text-xs text-secondary-700">{servicePrincipalCurl}</pre>
                </div>

                <div className="flex flex-col gap-2 border-t border-secondary-200 pt-4">
                  <p className="text-sm font-medium text-secondary-700">2. Use the token</p>
                  <div className="flex flex-wrap items-end gap-2">
                    <TextField
                      label="Access token"
                      className="flex-1 min-w-64"
                      value={bearerInput}
                      onChange={setBearerInput}
                      placeholder="Paste the access_token from the response…"
                      autoComplete="off"
                    />
                    <Button
                      variant="solid"
                      isDisabled={!bearerInput.trim()}
                      action={() => { setServicePrincipalToken(bearerInput.trim() || undefined); }}
                    >
                      Use token
                    </Button>
                    {servicePrincipalToken && (
                      <Button variant="soft" color="secondary" action={() => { setServicePrincipalToken(undefined); }}>
                        Clear
                      </Button>
                    )}
                  </div>
                  {servicePrincipalToken
                    ? <Badge color="positive">Documents and requests run as this service account</Badge>
                    : <span className="text-sm text-secondary-500">No token yet: showing the public surface.</span>}
                </div>
              </div>
            )}
      </Card>

      {specQuery.isLoading && (
        <div className="py-12 flex justify-center">
          <LoadingIndicator size={20} className="text-primary-700" />
        </div>
      )}

      {specQuery.isError && (
        <Card className="items-start">
          <p className="text-secondary-500">Could not load the API document.</p>
          <Button variant="soft" color="secondary" action={() => { void specQuery.refetch(); }}>Retry</Button>
        </Card>
      )}

      {specQuery.data && (
        <div className="rounded-lg border border-secondary-200 bg-white overflow-hidden">
          <SwaggerUI
            key={`${selectedId}:${authMode}:${activeToken ? 'auth' : 'anon'}`}
            spec={specQuery.data}
            docExpansion="list"
            defaultModelsExpandDepth={-1}
            plugins={[hideSwaggerAuthPlugin]}
            requestInterceptor={(req) => {
              const token = tokenRef.current;
              if (token) {
                req['headers'] = { ...(req['headers'] as Record<string, string>), Authorization: `Bearer ${token}` };
              }
              return req;
            }}
          />
        </div>
      )}
    </div>
  );
}
